import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import api from '../../services/api';
import LiveTracking from './LiveTracking';
import RatingModal from './RatingModal';
import { StarIcon } from '@heroicons/react/24/solid';
import { 
  ShareIcon, 
  ClipboardDocumentIcon, 
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface Stop {
  address: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  contact_name: string;
  contact_phone: string;
  contact_email?: string;
  delivery_instructions?: string;
}

interface Order {
  id: number;
  uuid: string;
  status: string;
  vehicle_type: string;
  package_description: string;
  package_type: string;
  package_weight_kg: number;
  dimensions_cm: string;
  fragile: boolean;
  stops: Stop[];
  total_distance_km: number;
  total_duration_minutes: number;
  base_price: number;
  final_price: number;
  payment_method: string;
  payment_reference?: string;
  payment_url?: string;
  required_pickup_time?: string;
  created_at: string;
  rider_id?: number;
  rider?: {
    first_name: string;
    last_name: string;
    phone: string;
    current_latitude?: number;
    current_longitude?: number;
    average_rating?: number;
    total_ratings?: number;
  };
}

interface OrderOTP {
  otp_code: string;
  expiry_time: string;
  status: string;
}

const OrderTracking: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscribe } = useWebSocket();
  const [order, setOrder] = useState<Order | null>(null);
  const [otp, setOtp] = useState<OrderOTP | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [realTimeStatus, setRealTimeStatus] = useState<string | null>(null);
  const [liveRiderPosition, setLiveRiderPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [checkingRating, setCheckingRating] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const getTrackingUrl = () => {
    if (!order?.uuid) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/track/${order.uuid}`;
  };

  const shareViaWhatsApp = () => {
    const url = getTrackingUrl();
    const message = `Track your Volexx delivery in real-time: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareViaSMS = () => {
    const url = getTrackingUrl();
    const message = `Track your Volexx delivery: ${url}`;
    window.open(`sms:?body=${encodeURIComponent(message)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareViaEmail = () => {
    const url = getTrackingUrl();
    const subject = `Track Your Volexx Delivery - Order #${order?.id}`;
    const body = `You can track your delivery in real-time using this link:\n\n${url}\n\nPowered by Volexx Logistics`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
    setShowShareMenu(false);
  };

  const copyTrackingLink = async () => {
    const url = getTrackingUrl();
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getBackUrl = () => {
    if (user?.role === 'admin') return '/admin/orders';
    if (user?.role === 'rider') return '/my-jobs';
    return '/orders';
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  useEffect(() => {
    const unsubscribeOrderUpdate = subscribe('order_update', (message) => {
      if (message.order_id === parseInt(orderId || '0')) {
        console.log('📩 Real-time order update:', message);
        setRealTimeStatus(message.status);
        fetchOrderDetails();
        
        if (message.message) {
          const notification = new Notification('Volexx Order Update', {
            body: message.message,
            icon: '/logo.png',
          });
        }
      }
    });

    const unsubscribeLocationUpdate = subscribe('location_update', (message) => {
      if (message.order_id === parseInt(orderId || '0')) {
        console.log('📍 Real-time location update:', message);
        
        // Update rider position immediately without refetching entire order
        if (message.latitude && message.longitude) {
          setLiveRiderPosition({
            lat: message.latitude,
            lng: message.longitude,
          });
        }
      }
    });

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    return () => {
      unsubscribeOrderUpdate();
      unsubscribeLocationUpdate();
    };
  }, [orderId, subscribe]);

  const checkIfRated = async () => {
    if (!orderId || user?.role !== 'customer') return;
    
    try {
      setCheckingRating(true);
      const response = await api.get(`/order/${orderId}/my-rating`);
      setHasRated(response.data.has_rated || false);
    } catch (error) {
      console.log('Rating check error:', error);
    } finally {
      setCheckingRating(false);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      setError('');
      
      // Fetch order details
      const orderResponse = await api.get(`/order/${orderId}`);
      setOrder(orderResponse.data);

      // Fetch OTP
      try {
        const otpResponse = await api.get(`/order/${orderId}/otp`);
        setOtp(otpResponse.data);
      } catch (otpError: any) {
        console.log('OTP not available yet:', otpError.response?.data?.detail);
      }
      
      // Check if user has rated (only for customers on delivered orders)
      if (orderResponse.data.status === 'delivered' && user?.role === 'customer') {
        await checkIfRated();
      }
      
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load order details');
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'pending_payment': 'bg-orange-100 text-orange-800',
      'assigned': 'bg-blue-100 text-blue-800',
      'in_transit': 'bg-purple-100 text-purple-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status: string) => {
    const text: Record<string, string> = {
      'pending': 'Pending Assignment',
      'pending_payment': 'Awaiting Payment',
      'assigned': 'Rider Assigned',
      'in_transit': 'In Transit',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return text[status] || status;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009FE3]"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 mb-4">{error || 'Order not found'}</p>
          <button
            onClick={() => navigate(getBackUrl())}
            className="px-6 py-2 bg-[#009FE3] text-white rounded-lg hover:bg-[#0089c9] transition"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const pickup = order.stops[0];
  const dropoff = order.stops[order.stops.length - 1];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(getBackUrl())}
          className="text-[#009FE3] hover:underline mb-4 flex items-center gap-2"
        >
          ← Back to Orders
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Order Tracking</h1>
        <p className="text-gray-600 mt-1">Order #{order.id}</p>
      </div>

      {/* Status Badge and Share Button */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <span className={`inline-block px-6 py-3 rounded-lg font-semibold text-lg ${getStatusBadgeColor(order.status)}`}>
          {getStatusText(order.status)}
        </span>
        
        {/* Share Tracking Button */}
        {user?.role === 'customer' && !['cancelled', 'delivered'].includes(order.status) && (
          <div className="relative">
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#009FE3] to-[#0089c9] text-white rounded-lg hover:from-[#0089c9] hover:to-[#007ab8] transition-all shadow-md"
            >
              <ShareIcon className="w-5 h-5" />
              <span className="font-medium">Share Tracking</span>
            </button>
            
            {/* Share Menu Dropdown */}
            {showShareMenu && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowShareMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                  <div className="p-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700">Share Tracking Link</span>
                      <button 
                        onClick={() => setShowShareMenu(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Let recipients track their delivery</p>
                  </div>
                  
                  <div className="p-2">
                    <button
                      onClick={shareViaWhatsApp}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-green-50 rounded-lg transition-colors group"
                    >
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">WhatsApp</p>
                        <p className="text-xs text-gray-500">Share via WhatsApp</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={shareViaSMS}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-blue-50 rounded-lg transition-colors group"
                    >
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">SMS</p>
                        <p className="text-xs text-gray-500">Share via text message</p>
                      </div>
                    </button>
                    
                    <button
                      onClick={shareViaEmail}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-purple-50 rounded-lg transition-colors group"
                    >
                      <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Email</p>
                        <p className="text-xs text-gray-500">Share via email</p>
                      </div>
                    </button>
                    
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={copyTrackingLink}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${linkCopied ? 'bg-green-500' : 'bg-gray-200'}`}>
                          {linkCopied ? (
                            <CheckIcon className="w-5 h-5 text-white" />
                          ) : (
                            <ClipboardDocumentIcon className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {linkCopied ? 'Link Copied!' : 'Copy Link'}
                          </p>
                          <p className="text-xs text-gray-500 truncate max-w-[160px]">
                            {getTrackingUrl()}
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Payment Button - Show for pending_payment orders */}
      {order.status === 'pending_payment' && order.payment_url && (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-6 mb-6">
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Complete Your Payment</h3>
            <p className="text-gray-600 mb-4">Your order is waiting for payment confirmation</p>
            <a
              href={order.payment_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#F15A29] hover:bg-[#d94f22] text-white font-bold py-4 px-8 rounded-lg transition-colors text-lg shadow-lg"
            >
              Pay Now - ₦{order.final_price?.toLocaleString()}
            </a>
            <p className="text-xs text-gray-500 mt-3">
              You will be redirected to Paystack to complete your payment securely
            </p>
          </div>
        </div>
      )}

      {/* OTP Display - Show for Customer, Admin, and Rider (when assigned) */}
      {(
        (user?.role === 'customer' && ['pending', 'assigned', 'in_transit'].includes(order.status)) ||
        (user?.role === 'admin') ||
        (user?.role === 'rider' && order.rider_id && ['assigned', 'in_transit'].includes(order.status))
      ) && (
        otp && otp.status === 'active' ? (
          <div className="bg-gradient-to-r from-[#009FE3] to-[#0089c9] text-white rounded-xl p-8 mb-6 shadow-lg">
            <div className="text-center">
              <p className="text-sm uppercase tracking-wide mb-2 opacity-90">
                {user?.role === 'rider' ? 'Customer Delivery Code' : 'Delivery Confirmation Code'}
              </p>
              <div className="text-6xl font-bold tracking-wider mb-4 font-mono">
                {otp.otp_code}
              </div>
              <p className="text-sm opacity-90">
                {user?.role === 'customer' && 'Share this code with the rider upon delivery'}
                {user?.role === 'admin' && 'Customer delivery confirmation code'}
                {user?.role === 'rider' && 'Enter this code to confirm delivery completion'}
              </p>
              <p className="text-xs mt-2 opacity-75">
                Valid until {new Date(otp.expiry_time).toLocaleString()}
              </p>
            </div>
          </div>
        ) : otp && otp.status === 'expired' ? (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-6">
            <div className="text-center">
              <p className="text-yellow-800 font-semibold mb-2">OTP Expired</p>
              <p className="text-sm text-yellow-700">
                {user?.role === 'rider' 
                  ? 'The delivery code has expired. Please contact support.'
                  : 'Your delivery code has expired. Please contact support if you need a new code.'}
              </p>
            </div>
          </div>
        ) : otp && otp.status === 'used' ? (
          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 mb-6">
            <div className="text-center">
              <p className="text-green-800 font-semibold mb-2">✓ Delivery Confirmed</p>
              <p className="text-sm text-green-700">
                This delivery has been confirmed with OTP verification.
              </p>
            </div>
          </div>
        ) : !otp && user?.role === 'customer' ? (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6 mb-6">
            <div className="text-center">
              <p className="text-blue-800 font-semibold mb-2">Delivery Code</p>
              <p className="text-sm text-blue-700">
                Your delivery confirmation code will be available once your order is processed.
              </p>
            </div>
          </div>
        ) : null
      )}

      {/* Live Tracking Map - Show when rider is assigned */}
      {order.rider_id && (
        <div className="mb-6">
          <LiveTracking 
            orderId={order.id} 
            autoRefresh={true} 
            refreshInterval={30000}
            liveRiderPosition={liveRiderPosition}
          />
        </div>
      )}

      {/* Rider Information */}
      {order.rider && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Rider Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-semibold">{order.rider.first_name} {order.rider.last_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Phone</p>
              <p className="font-semibold">{order.rider.phone}</p>
            </div>
            {(order.rider.average_rating !== null && order.rider.average_rating !== undefined) && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-600 mb-2">Rating</p>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon
                        key={star}
                        className={`h-5 w-5 ${
                          star <= Math.round(order.rider!.average_rating || 0)
                            ? 'text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="font-semibold text-gray-900">
                    {(order.rider.average_rating || 0).toFixed(1)}
                  </span>
                  <span className="text-sm text-gray-600">
                    ({order.rider.total_ratings || 0} {order.rider.total_ratings === 1 ? 'rating' : 'ratings'})
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Location Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Delivery Route</h2>
        
        {/* Pickup */}
        <div className="mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-green-600 mb-1">PICKUP</p>
              <p className="font-medium text-gray-900">{pickup.address}</p>
              <div className="mt-2 text-sm text-gray-600">
                <p><strong>Contact:</strong> {pickup.contact_name}</p>
                <p><strong>Phone:</strong> {pickup.contact_phone}</p>
                {pickup.contact_email && <p><strong>Email:</strong> {pickup.contact_email}</p>}
                {pickup.delivery_instructions && (
                  <p className="mt-1"><strong>Instructions:</strong> {pickup.delivery_instructions}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Connector Line */}
        <div className="ml-4 border-l-2 border-dashed border-gray-300 h-8"></div>

        {/* Dropoff */}
        <div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-[#F15A29] bg-opacity-20 rounded-full flex items-center justify-center mt-1">
              <div className="w-3 h-3 bg-[#F15A29] rounded-full"></div>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#F15A29] mb-1">DROPOFF</p>
              <p className="font-medium text-gray-900">{dropoff.address}</p>
              <div className="mt-2 text-sm text-gray-600">
                <p><strong>Contact:</strong> {dropoff.contact_name}</p>
                <p><strong>Phone:</strong> {dropoff.contact_phone}</p>
                {dropoff.contact_email && <p><strong>Email:</strong> {dropoff.contact_email}</p>}
                {dropoff.delivery_instructions && (
                  <p className="mt-1"><strong>Instructions:</strong> {dropoff.delivery_instructions}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Distance & Duration */}
        <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Distance</p>
            <p className="font-semibold">{order.total_distance_km.toFixed(1)} km</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Est. Duration</p>
            <p className="font-semibold">{order.total_duration_minutes} mins</p>
          </div>
        </div>
      </div>

      {/* Package Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Package Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Type</p>
            <p className="font-semibold capitalize">{order.package_type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Weight</p>
            <p className="font-semibold">{order.package_weight_kg} kg</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Dimensions</p>
            <p className="font-semibold">{order.dimensions_cm} cm</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Fragile</p>
            <p className="font-semibold">{order.fragile ? 'Yes' : 'No'}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm text-gray-600">Description</p>
            <p className="font-semibold">{order.package_description}</p>
          </div>
        </div>
      </div>

      {/* Service Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Service Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Vehicle Type</p>
            <p className="font-semibold capitalize">{order.vehicle_type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Pickup Time</p>
            <p className="font-semibold">
              {order.required_pickup_time 
                ? new Date(order.required_pickup_time).toLocaleString()
                : 'ASAP'}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Payment Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Payment Method</p>
            <p className="font-semibold capitalize">{order.payment_method}</p>
          </div>
          {order.payment_reference && (
            <div>
              <p className="text-sm text-gray-600">Reference</p>
              <p className="font-semibold text-xs">{order.payment_reference}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-600">Base Price</p>
            <p className="font-semibold">₦{order.base_price.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Amount</p>
            <p className="font-semibold text-lg text-[#009FE3]">₦{order.final_price.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Order Timeline */}
      <div className="mt-6 bg-gray-50 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-2">Order Created</h3>
        <p className="text-sm text-gray-600">{new Date(order.created_at).toLocaleString()}</p>
      </div>

      {/* Rating Button for Customers on Delivered Orders */}
      {user?.role === 'customer' && 
       order.status === 'delivered' && 
       order.rider_id && 
       !hasRated && 
       !checkingRating && (
        <div className="mt-6 bg-gradient-to-r from-sky-50 to-orange-50 rounded-xl p-6 border border-sky-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">How was your delivery?</h3>
              <p className="text-sm text-gray-600">
                Rate your experience with {order.rider?.first_name} {order.rider?.last_name}
              </p>
            </div>
            <button
              onClick={() => setShowRatingModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-sky-500 to-sky-600 text-white rounded-lg hover:from-sky-600 hover:to-sky-700 transition-all shadow-md hover:shadow-lg font-medium"
            >
              Rate Delivery
            </button>
          </div>
        </div>
      )}

      {/* Show thank you message if already rated */}
      {user?.role === 'customer' && 
       order.status === 'delivered' && 
       hasRated && (
        <div className="mt-6 bg-green-50 rounded-xl p-6 border border-green-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-green-900">Thank you for your feedback!</h3>
              <p className="text-sm text-green-700">Your rating helps us improve our service.</p>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && order.rider && (
        <RatingModal
          orderId={order.id}
          riderName={`${order.rider.first_name} ${order.rider.last_name}`}
          riderId={order.rider_id!}
          onClose={() => setShowRatingModal(false)}
          onSuccess={() => {
            setShowRatingModal(false);
            setHasRated(true);
          }}
        />
      )}
    </div>
  );
};

export default OrderTracking;
